import type { AnyAction } from 'redux';
import { Alert, Modal } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { IReduxState } from '@frontend/configureStore';
import { ToastType } from '@common/enums';
import { formatString } from '@common/utils/formatString';
import { updateAreEmploymentTaxesDetailsRequired } from '@frontend/store/authentication/actions';
import { getSelectedLanguage } from '@frontend/translate';
import { showToast, useAbortableFetch, useUiStateContext } from '@frontend/utils';
import { EmploymentTaxesDetailsAddEdit } from './EmploymentTaxesDetailsAddEdit';

interface IEmploymentTaxesDetailsModalProps {
    handleChangeIsOpen: (isOpen: boolean) => void;
    isOpen: boolean;
}

export function EmploymentTaxesDetailsModal(props: Readonly<IEmploymentTaxesDetailsModalProps>) {
    const selectedEmploymentId = useSelector((state: IReduxState) => state.authentication.selectedEmploymentId);
    const organizationId = useSelector((state: IReduxState) => state.authentication.organizationId);

    const [organizationName, setOrganizationName] = useState<string | null>(null);
    const { dispatchUiStateAction } = useUiStateContext();

    const dispatch = useDispatch();
    const abortableFetch = useAbortableFetch();

    useEffect(() => {
        loadOrganizationName();
    }, []);

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={() => {
                props.handleChangeIsOpen(false);
            }}
            size="large"
        >
            <Modal.Header>{getSelectedLanguage().employmentTaxesDetails.modal.title}</Modal.Header>
            <Modal.Body>
                <Alert
                    appearance="info"
                    hasIcon={true}
                    style={{ marginTop: 0 }}
                >
                    {getSelectedLanguage().employmentTaxesDetails.modal.infoText}
                    <Alert.Link
                        to={`/employments/${selectedEmploymentId}/personalSettings`}
                        variant="link"
                    >
                        {getSelectedLanguage().employmentTaxesDetails.modal.infoLinkText}
                    </Alert.Link>
                    .
                </Alert>
                <EmploymentTaxesDetailsAddEdit
                    onClose={() => {
                        props.handleChangeIsOpen(false);
                    }}
                    onSubmit={(dataCopiedToAllEmployments) => {
                        props.handleChangeIsOpen(false);
                        displayToastMessage(dataCopiedToAllEmployments);
                        dispatch(updateAreEmploymentTaxesDetailsRequired(false) as unknown as AnyAction);
                        dispatchUiStateAction({ bannerHeight: 0, type: 'setBannerHeight' });
                    }}
                />
            </Modal.Body>
        </Modal>
    );

    async function loadOrganizationName() {
        const result = await abortableFetch.fetchJson(`/api/organizations/${organizationId}`, 'GET');
        setOrganizationName(result.json.name);
    }

    function displayToastMessage(changeCopiedToAllEmployments?: boolean) {
        if (!changeCopiedToAllEmployments && organizationName) {
            showToast(
                ToastType.success,
                formatString(getSelectedLanguage().employmentTaxesDetails.singleOrganizationSuccessMessage, [
                    organizationName,
                ])
            );
        } else {
            showToast(
                ToastType.success,
                getSelectedLanguage().employmentTaxesDetails.multipleOrganizationSuccessMessage
            );
        }
    }
}
